<template>
  <div class="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
    <!-- Splash background image -->
    <img src="/splash_image.png" alt="Splash" class="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none select-none z-0" />

    <div class="w-full max-w-md p-4 z-10">
      <div class="bg-background-card rounded-xl shadow-card-elevated p-8">
        <!-- Logo Section -->
        <div class="flex items-center justify-center mb-6">
          <img src="/adam-cloud.png" alt="ADAM Cloud Logo" class="h-8 w-8 mr-2" />
          <div class="text-primary font-bold text-2xl">ADAM</div>
          <div class="text-secondary text-sm ml-1 mt-2">Cloud</div>
        </div>

        <!-- Welcome Text -->
        <div class="text-center mb-8">
          <h2 class="text-xl font-medium text-text">Welcome Back</h2>
          <p class="text-text-muted text-sm mt-1">Sign in to access your projects</p>
        </div>

        <!-- Login Form -->
        <form @submit.prevent="onSubmit" autocomplete="off">
          <div class="mb-5">
            <label class="block text-sm font-medium text-text-light mb-1" for="username">
              E-mail
            </label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="pi pi-user text-text-muted"></i>
              </div>
              <input
                id="username"
                v-model="email"
                type="text"
                class="w-full pl-10 pr-4 py-2.5 rounded-full border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-transparent"
                placeholder="Enter your e-mail"
                autocomplete="username"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="mb-6">
            <div class="flex justify-between items-center mb-1">
              <label class="block text-sm font-medium text-text-light" for="password">
                Password
              </label>
              <!-- <span class="text-sm text-primary hover:text-primary-light font-medium cursor-pointer" @click="onForgotPassword">
                Forgot Password?
              </span> -->
            </div>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <i class="pi pi-lock text-text-muted"></i>
              </div>
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="w-full pl-10 pr-10 py-2.5 rounded-full border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-transparent"
                placeholder="Enter your password"
                autocomplete="current-password"
                :disabled="loading"
              />
              <button type="button" @click="togglePassword" class="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none" tabindex="-1">
                <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" class="text-text-muted"></i>
              </button>
            </div>
          </div>

          <div class="mb-6">
            <button
              type="submit"
              class="w-full bg-accent hover:bg-accent-dark text-white rounded-full py-3 font-medium transition-colors flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              :disabled="loading"
            >
              <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
              <i v-else class="pi pi-sign-in mr-2"></i>
              <span>{{ loading ? 'Signing in...' : 'Sign In' }}</span>
            </button>
          </div>

          <div v-if="displayError" class="mb-6 animate-fade-in">
            <Message severity="error" icon="pi pi-exclamation-circle" class="w-full rounded-lg border border-warning/40 bg-warning/10 text-warning" :closable="false">
              <span class="text-sm">{{ error }}</span>
            </Message>
          </div>
        </form>

        <!-- Security Notice -->
        <div class="text-center text-xs text-text-muted">
          <div class="flex items-center justify-center mb-2">
            <i class="pi pi-shield text-primary mr-1"></i>
            <span class="font-medium">Secure Login</span>
          </div>
          <p>
            By signing in, you agree to ADAM Cloud's
            <span class="text-primary hover:underline cursor-pointer">Terms of Service</span>
            and
            <span class="text-primary hover:underline cursor-pointer">Privacy Policy</span>.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-6 text-center text-sm text-text-muted">
        <p>© 2025 ADAM Cloud. All rights reserved.</p>
        <p class="mt-1">Version {{ appVersion }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/shared/composables/useAuth';
import { useNavigationIntentStore } from '@/shared/stores/navigationIntent';
import { useProjectStore } from '@/shared/stores/project';

const appVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';

const email = ref('');
const password = ref('');
const showPassword = ref(false);
const router = useRouter();
const auth = useAuth();
const navigationIntent = useNavigationIntentStore();
const projectStore = useProjectStore();

const loading = auth.loading;
const error = auth.error;
const displayError = computed(() => Boolean(error.value) && !loading.value);

function togglePassword() {
  showPassword.value = !showPassword.value;
}

// function onForgotPassword() {
//   // You can route to a forgot password page or show a dialog
//   // router.push({ name: 'forgot-password' });
//   alert('Forgot password functionality is not implemented.');
// }

async function onSubmit() {
  if (!email.value.trim() || !password.value.trim()) {
    return;
  }
  const success = await auth.login(email.value, password.value);
  if (success) {
    auth.setRedirecting(true);
    try {
      const intendedRoute = navigationIntent.consumeIntendedRoute();
      const hasSelectedProject = Boolean(projectStore.selectedProjectId);

      if (intendedRoute) {
        const resolved = router.resolve(intendedRoute);
        const requiresProject = resolved.meta.requiresProject !== false;
        if (!requiresProject || hasSelectedProject) {
          await router.push(intendedRoute);
          return;
        }
      }

      await router.push({ path: '/' });
    } finally {
      navigationIntent.clearIntendedRoute();
      auth.setRedirecting(false);
    }
  }
}

definePage({
  meta: {
    title: 'Login to ADAM Cloud',
    requiresProject: false,
  }
});
</script>

<style scoped>
/* Hide scrollbars for this page */
::-webkit-scrollbar { display: none; }
html, body { -ms-overflow-style: none; scrollbar-width: none; }

@keyframes fade-in {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease;
}
</style>
